import type { Registry as Services } from '@ember/service';

export type PublishErrorKind = 'rejected' | 'unavailable' | 'invalid';

export interface IPublishError {
  status: number;
  code: string;
  detail?: string;
}

const TRANSLATION_PREFIX = 'connect_listings_publish.errors';

const INTERNAL_ERROR_CODE = 'internal_error';

export function extractPublishErrors(error: unknown): IPublishError[] {
  const errors = (error as { errors?: unknown } | undefined)?.errors;

  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.flatMap((entry: unknown): IPublishError[] => {
    const raw = entry as { status?: unknown; code?: unknown; detail?: unknown };
    const status = Number(raw?.status);

    if (
      !Number.isFinite(status) ||
      typeof raw?.code !== 'string' ||
      raw.code === INTERNAL_ERROR_CODE
    ) {
      return [];
    }

    if (typeof raw.detail === 'string' && raw.detail.length > 0) {
      return [{ status, code: raw.code, detail: raw.detail }];
    }

    return [{ status, code: raw.code }];
  });
}

export function publishErrorKind(
  errors: IPublishError[],
): PublishErrorKind | undefined {
  const status = errors[0]?.status;

  if (status === undefined) {
    return undefined;
  }

  if (status === 422) {
    return 'rejected';
  }

  if (status === 400) {
    return 'invalid';
  }

  return 'unavailable';
}

export function publishErrorMessage(
  error: IPublishError,
  intl: Services['intl'],
): string | undefined {
  const key = `${TRANSLATION_PREFIX}.${error.code}`;

  if (intl.exists(key)) {
    return intl.t(key, { detail: error.detail ?? '' });
  }

  return error.detail;
}

export function shouldReportPublishError(error: unknown): boolean {
  return !(error as { isAdapterError?: boolean } | undefined)?.isAdapterError;
}
